import {
    Button,
    FlexBox,
    H2,
    H3,
    Icon,
    Link,
    P,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    tokensRaw,
} from '@vp/swan';
import styled from 'styled-components';
import { sendNavigationEvent, usePage } from '@99designs/tracking';
import { ProductTierSection } from '../ProductTierSection';
import { baseFeatures } from '../pageData/featuresConfig';
import { BASE_TIER } from '../pageData/tierConfig';
import clickEventData from '../utils/clickEventData';
import { Popover } from './Popover';
import { TierCard } from './TierCard';
import type { FeatureKey } from '../pageData/featuresConfig';
import type { Tier } from '../pageData/tierConfig';

interface TierComparisonProps {
    tiers: Tier[];
}

const StyledFlexBox = styled(FlexBox)`
    @media (min-width: ${tokensRaw.SwanBaseBreakpointXsEnd}) {
        display: none !important;
    }
`;

const StyledTable = styled(Table)`
    table-layout: fixed;

    @media (max-width: ${tokensRaw.SwanBaseBreakpointXsEnd}) {
        height: 100%;
        display: none !important;
    }
`;

const StyledHeading = styled(H2)`
    @media (max-width: ${tokensRaw.SwanBaseBreakpointXsEnd}) {
        display: none !important;
    }
`;

type FeatureRow = {
    key: FeatureKey;
};

export function TierComparison({ tiers }: TierComparisonProps) {
    const { pageData } = usePage();
    const inclusionFeatureRows: FeatureRow[] = [
        { key: 'suitableFor' },
        { key: 'turnaroundTime' },
        { key: 'revisions' },
        { key: 'designerMatching' },
        { key: 'fileFormat' },
    ];

    const communicationFeatureRows: FeatureRow[] = [
        { key: 'revisionComments' },
        { key: 'messaging' },
    ];

    const pageZone = 'Tier Tiles';

    return (
        <>
            {/* Breakpoint = XS */}
            <StyledFlexBox flexDirection="column" gap={4} m={3}>
                <H3>Compare Packages</H3>
                {tiers.map((tier) => (
                    <TierCard key={tier.id} tier={tier} />
                ))}
            </StyledFlexBox>
            {/* Breakpoint > XS */}
            <StyledHeading>Compare Packages</StyledHeading>
            <StyledTable width="100%">
                <TableHead
                    style={{
                        position: 'sticky',
                        top: 0,
                        zIndex: 1,
                    }}
                >
                    <TableRow style={{ borderStyle: 'none' }}>
                        <TableCell width="25%" />
                        {tiers.map((tier) => {
                            const briefURL = tier.briefURL;
                            const ctaValue =
                                tier.id === BASE_TIER
                                    ? 'Simple Get Started-CTA'
                                    : 'Exclusive Get Started-CTA';
                            return (
                                <TableCell
                                    key={tier.id}
                                    textAlign="left"
                                    p={5}
                                    pt={7}
                                    bgc={setBackgroundColor(tier)}
                                >
                                    <ProductTierSection tier={tier} price={tier.price} />
                                    <Button
                                        mt={3}
                                        skin="primary"
                                        width="full-width"
                                        render={({ children, ...rest }) => (
                                            <Link
                                                to={briefURL}
                                                onClick={() => {
                                                    sendNavigationEvent({
                                                        ...clickEventData({
                                                            pagePath: pageData.pagePath,
                                                            ctaUrl: briefURL,
                                                            ctaValue,
                                                            pageZone,
                                                        }),
                                                        ...pageData,
                                                    });
                                                }}
                                                {...rest}
                                            >
                                                {children}
                                            </Link>
                                        )}
                                    >
                                        Continue to brief
                                    </Button>
                                </TableCell>
                            );
                        })}
                    </TableRow>
                </TableHead>
                <TableBody>
                    <TableRow>
                        <TableCell p={1}>
                            <P fontSize="large" fontWeight="bold">
                                Inclusions
                            </P>
                        </TableCell>
                        <TableCell />
                        <TableCell bgc="strong" />
                    </TableRow>
                    {inclusionFeatureRows.map((row) => (
                        <TableRow key={row.key}>
                            <TableCell py={0} pl={4}>
                                <FlexBox alignItems="center" justifyContent="space-between">
                                    <P>{baseFeatures[row.key].title}</P>
                                    {baseFeatures[row.key].popover && (
                                        <Popover content={baseFeatures[row.key].popover} />
                                    )}
                                </FlexBox>
                            </TableCell>
                            {tiers.map((tier: Tier) => {
                                return (
                                    <TableCell
                                        py={0}
                                        key={tier.id}
                                        textAlign="center"
                                        bgc={setBackgroundColor(tier)}
                                    >
                                        {getInclusionsDescription(tier, row.key)}
                                    </TableCell>
                                );
                            })}
                        </TableRow>
                    ))}
                    <TableRow>
                        <TableCell p={0} pt={6}>
                            <P fontSize="large" fontWeight="bold">
                                Communications
                            </P>
                        </TableCell>
                        <TableCell />
                        <TableCell bgc="strong" />
                    </TableRow>
                    {communicationFeatureRows.map((row) => (
                        <TableRow key={row.key}>
                            <TableCell py={0} pl={4}>
                                <FlexBox alignItems="center" justifyContent="space-between">
                                    <P>{baseFeatures[row.key].title}</P>
                                    {baseFeatures[row.key].popover && (
                                        <Popover content={baseFeatures[row.key].popover} />
                                    )}
                                </FlexBox>
                            </TableCell>
                            {tiers.map((tier) => (
                                <TableCell
                                    key={tier.id}
                                    textAlign="center"
                                    bgc={setBackgroundColor(tier)}
                                >
                                    {getCommunicationsDescription(tier, row.key)}
                                </TableCell>
                            ))}
                        </TableRow>
                    ))}
                </TableBody>
            </StyledTable>
        </>
    );
}

const getInclusionsDescription = (tier: Tier, featureKey: FeatureKey) => {
    const feature = tier.features[featureKey];

    if (featureKey === 'turnaroundTime') {
        const descriptionText = feature.description?.text || '';
        return descriptionText.replace('turnaround', '(excludes weekends and public holidays)');
    }

    return feature.mobileDescription || feature.description?.text || '';
};

const getCommunicationsDescription = (tier: Tier, featureKey: FeatureKey) => {
    const feature = tier.features[featureKey];

    if (tier.id === BASE_TIER && featureKey === 'messaging') {
        return feature.description?.text || '';
    }

    return <Icon iconType="check" size="standard" skin="success" />;
};
function setBackgroundColor(tier: Tier) {
    return tier.id === 'exclusiveDesign' ? 'strong' : 'standard';
}
